import { useEffect } from "react";
import "./AchievementUnlocked.css";

/**
 * AchievementUnlocked — one-shot cinematic for milestone moments
 * (currently fires the first time a user defeats each NPC Trainer).
 *
 * Visual: a wide gold banner slides in from the top, holds with a soft
 * shimmer for ~3s, then slides out. Includes the trainer portrait,
 * faction tag, and the "Achievement Unlocked" subtitle. Deliberately
 * different from `PayoutFlash` so the player can tell at a glance
 * "this is a milestone, not a normal win".
 *
 * Props:
 *   show      bool   — render & start the animation when true
 *   title     str    — main line (e.g. "Free Pal Alliance Scout Mira")
 *   subtitle  str    — small caption (defaults to "Trainer Defeated")
 *   portrait  str    — absolute URL to the trainer's signature pal image
 *   duration  ms     — total on-screen time (default 3600)
 *   onDone    fn     — fires after the banner finishes
 */
export function AchievementUnlocked({
  show,
  title,
  subtitle = "Trainer Defeated",
  portrait,
  duration = 3600,
  onDone,
}) {
  useEffect(() => {
    if (!show) return;
    const t = setTimeout(() => onDone?.(), duration);
    return () => clearTimeout(t);
  }, [show, duration, onDone]);

  if (!show) return null;

  return (
    <div
      className="achievement-unlocked"
      role="presentation"
      onClick={() => onDone?.()}
      data-testid="achievement-unlocked"
    >
      <div className="achievement-unlocked__banner">
        <div className="achievement-unlocked__shimmer" aria-hidden="true" />
        {portrait && (
          <div className="achievement-unlocked__portrait">
            <img
              src={portrait}
              alt=""
              draggable={false}
              onError={(e) => { e.currentTarget.style.opacity = 0.25; }}
            />
          </div>
        )}
        <div className="achievement-unlocked__text">
          <div className="achievement-unlocked__eyebrow">
            <span className="achievement-unlocked__star">★</span>
            Achievement Unlocked
          </div>
          <div className="achievement-unlocked__title" data-testid="achievement-title">
            {title}
          </div>
          <div className="achievement-unlocked__subtitle">{subtitle}</div>
        </div>
      </div>
    </div>
  );
}
